import React, { useState } from "react";
import {
  Card,
  CardBody,
  Container,
  Row,
  Col,
  Form,
  Label,
  Input,
  Button,
  Spinner,
} from "reactstrap";
import { useNavigate, useLocation } from "react-router-dom";
import { CustomSnackbar } from "../../../helpers/CustomSnackbar/index";
import { resetPassword } from "../../../api/customer";
import { apiErrorHandler } from "../../../helpers/common";

const RetypePassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  // Retrieve email passed via location state
  const emailFromState = location.state?.email || "";
  const phoneFromState = location.state?.phoneNumber || "";
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
    email: emailFromState,
    phoneNumber: phoneFromState,
  });

  const onChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log("🚀 handleSubmit triggered successfully!");

    if (!formData.password) {
      CustomSnackbar.error("Please enter new password");
      return;
    }

    if (formData.password.length < 8) {
      CustomSnackbar.error("Password should be at least 8 characters");
      return;
    }

    if (!formData.confirmPassword) {
      CustomSnackbar.error("Please confirm your password");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      CustomSnackbar.error("Passwords do not match");
      return;
    }

    try {
      setLoading(true);

      // ✅ Clean Payload: Passing only what the backend needs since the token is handled automatically
      const payload = {
        password: formData.password,
        retypePassword: formData.confirmPassword,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
      };

      console.log("📡 Sending API Request with Payload:", payload);

      const response = await resetPassword(payload);

      console.log("📥 API Server Raw Response:", response);

      if (response?.data?.success) {
        CustomSnackbar.success(
          response?.data?.message || "Password changed successfully",
        );
        navigate("/signin");
      } else {
        CustomSnackbar.error(
          response?.data?.message || "Failed to reset password",
        );
      }
    } catch (error) {
      console.error("🚨 HTTP Network Request Interception Exception:", error);
      apiErrorHandler(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="mt-5">
      <Row className="justify-content-center">
        <Col lg={5} md={7}>
          <Card className="shadow border-0 rounded-4">
            <CardBody className="p-5">
              <div className="text-center mb-4">
                <h3 className="fw-bold">Reset Password</h3>
                <p className="text-muted mb-0">
                  Create a new password for your account.
                </p>
              </div>

              <Form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <Label>New Password</Label>
                  <div className="position-relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      placeholder="Enter new password"
                      value={formData.password}
                      onChange={onChange}
                      className="pe-5"
                    />
                    <span
                      onClick={() => setShowPassword((prev) => !prev)}
                      style={{
                        position: "absolute",
                        right: "12px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                        zIndex: 10,
                        color: "#0d6efd", // Primary Blue
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {showPassword ? (
                        /* Eye Slash Icon (Blue) */
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          fill="currentColor"
                          viewBox="0 0 16 16"
                        >
                          <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7 7 0 0 0-2.79.588l.77.771A6 6 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13 13 0 0 1 14.828 8q-.086.13-.195.288c-.335.48-.83 1.12-1.465 1.755-.165.165-.337.328-.517.486z" />
                          <path d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z" />
                          <path d="M3.35 5.47q-.27.242-.534.509C1.408 7.37 0 8 0 8s3 5.5 8 5.5c1.196 0 2.298-.25 3.275-.68l-.75-.75c-.752.288-1.574.43-2.525.43-2.12 0-3.879-1.168-5.168-2.457A13 13 0 0 1 1.172 8z" />
                          <path d="M13.646 14.354l-12-12 .708-.708 12 12z" />
                        </svg>
                      ) : (
                        /* Eye Icon (Blue) */
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          fill="currentColor"
                          viewBox="0 0 16 16"
                        >
                          <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8M1.173 8a13 13 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5s3.879 1.168 5.168 2.457A13 13 0 0 1 14.828 8q-.086.13-.195.288c-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5s-3.879-1.168-5.168-2.457A13 13 0 0 1 1.172 8z" />
                          <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5M4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0" />
                        </svg>
                      )}
                    </span>
                  </div>
                </div>

                <div className="mb-4">
                  <Label>Confirm Password</Label>
                  <div className="position-relative">
                    <Input
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      placeholder="Confirm password"
                      value={formData.confirmPassword}
                      onChange={onChange}
                      className="pe-5"
                    />
                    <span
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                      style={{
                        position: "absolute",
                        right: "12px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                        zIndex: 10,
                        color: "#0d6efd", // Primary Blue
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {showConfirmPassword ? (
                        /* Eye Slash Icon (Blue) */
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          fill="currentColor"
                          viewBox="0 0 16 16"
                        >
                          <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7 7 0 0 0-2.79.588l.77.771A6 6 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13 13 0 0 1 14.828 8q-.086.13-.195.288c-.335.48-.83 1.12-1.465 1.755-.165.165-.337.328-.517.486z" />
                          <path d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z" />
                          <path d="M3.35 5.47q-.27.242-.534.509C1.408 7.37 0 8 0 8s3 5.5 8 5.5c1.196 0 2.298-.25 3.275-.68l-.75-.75c-.752.288-1.574.43-2.525.43-2.12 0-3.879-1.168-5.168-2.457A13 13 0 0 1 1.172 8z" />
                          <path d="M13.646 14.354l-12-12 .708-.708 12 12z" />
                        </svg>
                      ) : (
                        /* Eye Icon (Blue) */
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          fill="currentColor"
                          viewBox="0 0 16 16"
                        >
                          <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8M1.173 8a13 13 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5s3.879 1.168 5.168 2.457A13 13 0 0 1 14.828 8q-.086.13-.195.288c-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5s-3.879-1.168-5.168-2.457A13 13 0 0 1 1.172 8z" />
                          <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5M4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0" />
                        </svg>
                      )}
                    </span>
                  </div>
                </div>

                <Button
                  type="submit"
                  color="primary"
                  className="w-100"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Updating...
                    </>
                  ) : (
                    "Reset Password"
                  )}
                </Button>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default RetypePassword;


